import React, { memo } from "react";

import { transformDateToLocalDateString } from "helpers";
import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";
import {
  countTires,
  formatTireDimension,
  TIRE_SET_INTRO_SAMPLE,
} from "ui/modules/fleet/tires/pages/TireSetEditCard/components/TireSetIntro/consts";

import { getDemountVehicle, getDemountWarehouse, TIRE_SET_DEMOUNT_MIN_PROFILE_DEPTH } from "../consts";
import { TireSetDemountSceneProps } from "../types";

/** Korak "Skladište": set guma s vozila ide na skladište, uz provjeru dubine profila prije spremanja. */
const DemountWarehouseScene: React.FC<TireSetDemountSceneProps> = (props) => {
  const { currentSet, formValues } = props;
  const vehicle = getDemountVehicle(props);
  const warehouseName = getDemountWarehouse(props);
  const code = currentSet.code || TIRE_SET_INTRO_SAMPLE.code;
  const dimension = formatTireDimension(currentSet.tires[0]) || TIRE_SET_INTRO_SAMPLE.dimension;
  const quantity = countTires(currentSet.tires) || TIRE_SET_INTRO_SAMPLE.quantity;
  const date = transformDateToLocalDateString(formValues.transferDate ?? new Date());

  // Dubina profila po gumi; dok nije upisana, primjer.
  const depths = currentSet.tires.some((tire) => tire.profileDepth)
    ? currentSet.tires.flatMap((tire) => Array.from({ length: tire.quantity ?? 1 }, () => tire.profileDepth ?? 0)).slice(0, 4)
    : TIRE_SET_INTRO_SAMPLE.profileDepths;

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          {/* Slika ima bijelu podlogu kao kartica, pa bez okvira. */}
          <div className="h-[5.5rem] overflow-hidden">
            <img src={TIRE_SET_INTRO_SAMPLE.image} alt="" className="w-full h-full object-contain scale-[1.35]" />
          </div>
          <div className="mt-2 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">{code}</div>
          <div className="text-sm font-bold text-slate-800 truncate">
            {dimension} · {quantity} kom
          </div>

          {/* Ruta: vozilo gore, skladište dolje, isprekidana crta između. */}
          <div className="mt-3 flex gap-3">
            <div className="flex flex-col items-center pt-1">
              <span className="w-3 h-3 rounded-full border-2 border-slate-400 bg-white" />
              <span className="flex-1 w-px my-1 border-l-2 border-dashed border-slate-300" />
              <span className="w-3 h-3 rounded-full bg-blue-600 ring-4 ring-blue-100" />
            </div>
            <div className="flex-1 min-w-0 space-y-3">
              <div>
                <div className="text-[0.6rem] text-slate-400">Vozilo</div>
                <div className="text-[0.75rem] font-semibold text-slate-500 truncate">
                  {vehicle.name} · {vehicle.registration}
                </div>
              </div>
              <div>
                <div className="text-[0.6rem] text-blue-500 font-semibold">Skladište · {date}</div>
                <div className="text-sm font-bold text-slate-800 truncate">{warehouseName}</div>
              </div>
            </div>
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Dubina profila</div>
          <div className="mt-3 flex items-end justify-between gap-2 h-16">
            {depths.map((depth, index) => (
              <div key={index} className="flex flex-1 flex-col items-center gap-1">
                <div
                  className={depth >= TIRE_SET_DEMOUNT_MIN_PROFILE_DEPTH ? "w-full rounded-sm bg-emerald-400" : "w-full rounded-sm bg-amber-400"}
                  style={{ height: `${Math.min(depth / 8, 1) * 3.5}rem` }}
                />
                <span className="text-[0.55rem] font-semibold text-slate-500">{depth} mm</span>
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Provjerite dubinu profila"
          subtitle="Prije nego set ode na policu"
          iconClassName="bg-emerald-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="4" />
              <path d="M12 3v5M12 16v5M3 12h5M16 12h5" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(DemountWarehouseScene);
